// § 1 · Status & Controls
// "What is this source doing right now?"
//
// The single source of truth for source state in v2. Operator can read
// status + last activity at-a-glance, and pause/resume/archive in one
// click without leaving the section.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Source, TriggerRun } from '@datahub/shared';
import { archiveSource, getSource, listSourceErrors, listTriggers, patchSource } from '../../api';
import { useToast } from '../Toast';
import { SectionShell } from './SectionShell';

interface Props {
  source: Source;
  onSourceChange: (s: Source) => void;
}

function fmtRelative(iso?: string): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return 'in the future';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

export function StatusControlsV2({ source, onSourceChange }: Props): React.ReactElement {
  const toast = useToast();
  const navigate = useNavigate();
  const [pending, setPending] = useState<'pause' | 'resume' | 'archive' | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(source.name);
  const [activeTriggerCount, setActiveTriggerCount] = useState<number | null>(null);
  const [openErrorCount, setOpenErrorCount] = useState<number | null>(null);

  // Fetch trigger + error counts in parallel — purely for at-a-glance
  // status. Failures swallowed (counts render as "—").
  useEffect(() => {
    let alive = true;
    Promise.all([
      listTriggers(source.source_id).catch(() => null),
      listSourceErrors(source.source_id, 50).catch(() => null) as Promise<TriggerRun[] | null>,
    ]).then(([triggers, runs]) => {
      if (!alive) return;
      setActiveTriggerCount(triggers ? triggers.filter(t => t.status === 'active').length : null);
      const oneHourAgo = Date.now() - 60 * 60 * 1000;
      setOpenErrorCount(runs ? runs.filter(r => r.status === 'failure' && new Date(r.ran_at).getTime() > oneHourAgo).length : null);
    });
    return () => { alive = false; };
  }, [source.source_id]);

  // Status meta: label + colour + dot.
  const stateLabel = source.status === 'archived' ? 'ARCHIVED' : source.status === 'paused' ? 'PAUSED' : 'LIVE';
  const stateColor = source.status === 'archived' ? 'var(--ink-3)'
    : source.status === 'paused' ? 'oklch(0.65 0.13 60)'
    : 'oklch(0.62 0.18 145)';

  async function togglePause() {
    if (pending) return;
    const next = source.status === 'paused' ? 'active' : 'paused';
    setPending(next === 'paused' ? 'pause' : 'resume');
    try {
      const updated = await patchSource(source.source_id, { status: next });
      onSourceChange(updated);
      toast.show(next === 'paused' ? 'Paused — webhook still 202s, no triggers fire.' : 'Resumed.', { kind: 'success' });
    } catch (e) {
      toast.show(`State change failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setPending(null);
    }
  }

  async function archive() {
    if (pending) return;
    if (!window.confirm(`Archive "${source.name}"?\n\nWebhook returns 410 immediately. Data preserved 90 days. Restore via Settings.`)) return;
    setPending('archive');
    try {
      await archiveSource(source.source_id);
      toast.show('Archived. Redirecting…', { kind: 'success' });
      setTimeout(() => navigate('/sources'), 800);
    } catch (e) {
      toast.show(`Archive failed: ${(e as Error).message}`, { kind: 'error' });
      setPending(null);
    }
  }

  async function saveName() {
    const trimmed = draftName.trim();
    if (!trimmed || trimmed === source.name) { setEditingName(false); return; }
    try {
      const updated = await patchSource(source.source_id, { name: trimmed });
      onSourceChange(updated);
      toast.show('Name updated.', { kind: 'success' });
    } catch (e) {
      toast.show(`Rename failed: ${(e as Error).message}`, { kind: 'error' });
      setDraftName(source.name);
    }
    setEditingName(false);
  }

  function copy(s: string, label: string) {
    void navigator.clipboard.writeText(s);
    toast.show(`${label} copied.`, { kind: 'success' });
  }

  // Refresh the source meta + counts on a 30s tick so "active 2m ago"
  // stays honest without manual reload.
  useEffect(() => {
    const t = setInterval(() => {
      getSource(source.source_id).then(s => onSourceChange(s)).catch(() => undefined);
    }, 30000);
    return () => clearInterval(t);
  }, [source.source_id, onSourceChange]);

  return (
    <SectionShell
      id="status"
      title="Status"
      subtitle="Live state of this source"
      accent="primary"
      dense
    >
      <div style={{ display: 'grid', gap: 14 }}>
        {/* Row 1 — name + ID + state pill */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', fontFamily: 'var(--mono)', fontSize: 10,
            background: 'var(--bg-sunken)', borderRadius: 12,
            color: stateColor, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600,
          }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: stateColor }} />
            {stateLabel}
          </span>
          {editingName ? (
            <input
              autoFocus
              value={draftName}
              onChange={e => setDraftName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void saveName(); if (e.key === 'Escape') { setDraftName(source.name); setEditingName(false); } }}
              onBlur={() => void saveName()}
              style={{ flex: 1, minWidth: 200, fontSize: 16, fontWeight: 500, padding: '4px 8px', border: '1px solid var(--accent-line)', borderRadius: 4, fontFamily: 'inherit' }}
            />
          ) : (
            <button
              onClick={() => { setDraftName(source.name); setEditingName(true); }}
              title="Click to rename"
              style={{ flex: 1, minWidth: 200, textAlign: 'left', padding: '4px 8px', background: 'transparent', border: 'none', fontSize: 16, fontWeight: 500, cursor: 'pointer', color: 'var(--ink)' }}
            >{source.name}</button>
          )}
          <code
            onClick={() => copy(source.source_id, 'Source ID')}
            title="Click to copy"
            style={{ padding: '3px 8px', background: 'var(--bg-sunken)', borderRadius: 4, fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-3)', cursor: 'pointer' }}
          >{source.source_id}</code>
        </div>

        {/* Row 2 — at-a-glance metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          <Metric label="Last event" value={fmtRelative(source.last_event_at)} />
          <Metric label="Active triggers" value={activeTriggerCount === null ? '—' : String(activeTriggerCount)} />
          <Metric
            label="Open errors (1h)"
            value={openErrorCount === null ? '—' : String(openErrorCount)}
            tone={openErrorCount && openErrorCount > 0 ? 'danger' : undefined}
            onClick={openErrorCount && openErrorCount > 0 ? () => {
              document.getElementById('v2-data')?.scrollIntoView({ behavior: 'smooth' });
              window.dispatchEvent(new CustomEvent('v2-data-tab', { detail: 'errors' }));
            } : undefined}
          />
          <Metric label="Created" value={fmtRelative(source.created_at)} />
        </div>

        {/* Row 3 — controls */}
        {source.status !== 'archived' && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', paddingTop: 4, borderTop: '1px dashed var(--line)' }}>
            <span style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginRight: 4 }}>Controls</span>
            <button
              className="btn sm"
              onClick={togglePause}
              disabled={pending !== null}
              title={source.status === 'paused' ? 'Resume — accept events again' : 'Pause — webhook keeps responding 202 but triggers do not fire'}
              style={{ color: source.status === 'paused' ? 'oklch(0.62 0.18 145)' : 'var(--ink-2)' }}
            >
              {pending === 'pause' ? 'pausing…' : pending === 'resume' ? 'resuming…' : source.status === 'paused' ? '▶ resume' : '⏸ pause'}
            </button>
            <button
              className="btn sm ghost"
              onClick={archive}
              disabled={pending !== null}
              title="Archive — webhook returns 410, data preserved 90 days"
              style={{ color: 'var(--danger)' }}
            >
              {pending === 'archive' ? 'archiving…' : 'archive'}
            </button>
          </div>
        )}
      </div>
    </SectionShell>
  );
}

function Metric({ label, value, tone, onClick }: { label: string; value: string; tone?: 'danger'; onClick?: () => void }): React.ReactElement {
  const cellStyle = {
    padding: '8px 12px',
    background: 'var(--bg-sunken)',
    border: '1px solid var(--line)',
    borderRadius: 6,
    cursor: onClick ? 'pointer' : 'default',
    color: tone === 'danger' ? 'var(--danger)' : 'inherit',
  } as const;
  const inner = (
    <>
      <div style={{ fontSize: 10, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500, marginTop: 2 }}>{value}</div>
    </>
  );
  return onClick
    ? <button onClick={onClick} style={{ ...cellStyle, textAlign: 'left', font: 'inherit' }}>{inner}</button>
    : <div style={cellStyle}>{inner}</div>;
}
